import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Modal,
  Radio,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import { Checkbox } from 'antd/lib';
import * as yup from 'yup';

import { InputForm } from '@/components/form/Input';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { airExchangeTableSelectors } from '@/core/redux/slices/airExchangeTable/selectors';
import { airExchangeTableActions } from '@/core/redux/slices/airExchangeTable/slice';
import { reportActions } from '@/core/redux/slices/smokeExtraction/system/report/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';

const { Text, Title } = Typography;

interface IDataSystemTable {
  nameSystem: string;
  air: number;
}

const ModalContent: React.FC = () => {
  const [type, setType] = useState<string>('view');
  const [typeSystem, setTypeSystem] = useState<string>('sys_inflow');
  const [currentRooms, setCurrentRooms] = useState<string[]>([]);

  const dispatch = useAppDispatch();

  const { isRender } = useAppSelector(
    airExchangeTableSelectors.airExchangeTableSystemModal
  );

  const system = useAppSelector(systemSelectors.system);

  const dataSystems = (): IDataSystemTable[] => {
    const arrData: any = [];

    system?.state?.table?.map((el: any) => {
      if (el?.sys_inflow && el?.flow_inflow)
        arrData.push({ nameSystem: el?.sys_inflow, air: el?.flow_inflow });
      if (el?.sys_exhaust && el?.flow_exhaust)
        arrData.push({ nameSystem: el?.sys_exhaust, air: el?.flow_exhaust });
      if (el?.sys_mo_inflow && el?.flow_mo_inflow)
        arrData.push({
          nameSystem: el?.sys_mo_inflow,
          air: el?.flow_mo_inflow,
        });
      if (el?.sys_mo_exhaust && el?.flow_mo_exhaust)
        arrData.push({
          nameSystem: el?.sys_mo_exhaust,
          air: el?.flow_mo_exhaust,
        });
    });

    const result: any = {};

    arrData.forEach((item: any) => {
      const { nameSystem, air } = item;

      // Проверяем, есть ли уже такой ключ в результате
      if (!result.hasOwnProperty(nameSystem)) {
        // Если нет, создаем новую запись
        result[nameSystem] = { nameSystem, air: 0 };
      }

      // Добавляем текущее значение air к накопленному
      result[nameSystem].air += air;
    });

    return Object.values(result);
  };

  const schema = yup.object().shape({
    name_system: yup.string().required('Это поле обязательно для заполнения'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const radioOptions: CheckboxGroupProps<string>['options'] = [
    {
      label: (
        <div>
          <CaretDownOutlined /> Приточная
        </div>
      ),
      value: 'sys_inflow',
      style: { flexGrow: 1 },
    },
    {
      label: (
        <div>
          <CaretUpOutlined /> Вытяжная
        </div>
      ),
      value: 'sys_exhaust',
      style: { flexGrow: 1 },
    },
  ];

  const getTitle = () => {
    switch (type) {
      case 'view':
        return 'Расход воздуха по системам';
      case 'add':
        return 'Новая система';
      case 'change':
        return 'Редактировать систему';
      default:
        break;
    }
  };

  const closeModal = () => {
    dispatch(
      airExchangeTableActions.setAirExchangeTableSystemModal({
        isRender: false,
      })
    );
  };

  const handleChangeCurrentRooms = (id: string) => {
    if (currentRooms?.includes(id)) {
      setCurrentRooms((prev) => prev?.filter((el) => el !== id));
    } else {
      setCurrentRooms([...currentRooms, id]);
    }
  };

  const roomColumns: TableColumnsType = [
    {
      title: 'Корпус',
      dataIndex: 'block',
      align: 'center',
    },
    {
      title: 'Этаж',
      dataIndex: 'floor',
      align: 'center',
    },
    {
      title: '№',
      dataIndex: 'num',
      align: 'center',
    },
    {
      title: 'Наименование помещения',
      dataIndex: 'name',
      align: 'center',
    },
    {
      title: (
        <div className='text-nowrap'>
          L, м<sup>3</sup>/ч
        </div>
      ),
      dataIndex: typeSystem === 'sys_inflow' ? 'flow_inflow' : 'flow_exhaust',
      align: 'center',
    },

    {
      title: '',
      dataIndex: 'check',
      render: (text, record, index) => (
        <Checkbox
          checked={currentRooms?.includes(record?.id)}
          onChange={() => handleChangeCurrentRooms(record?.id)}
        />
      ),
      align: 'end',
    },
  ];

  const columns: TableColumnsType = [
    {
      title: 'Система',
      dataIndex: 'nameSystem',
      align: 'center',
    },
    {
      title: (
        <>
          Расход, м<sup>3</sup>/ч
        </>
      ),
      dataIndex: 'air',
      align: 'center',
    },
  ];

  const dataRooms = system?.state?.table?.filter((el: any) => {
    if (typeSystem === 'sys_inflow') {
      if (
        !el?.[typeSystem] &&
        el?.area &&
        el?.height &&
        el?.volume &&
        ((el?.flow_inflow && !el?.flow_exhaust) ||
          (!el?.flow_inflow && !el?.flow_exhaust) ||
          (!!el?.flow_inflow && !!el?.flow_exhaust))
      )
        return el;
    }

    if (typeSystem === 'sys_exhaust') {
      if (
        !el?.[typeSystem] &&
        el?.area &&
        el?.height &&
        el?.volume &&
        ((!el?.flow_inflow && el?.flow_exhaust) ||
          (!el?.flow_inflow && !el?.flow_exhaust) ||
          (!!el?.flow_inflow && !!el?.flow_exhaust))
      )
        return el;
    }
  });

  const handleSelectAll = () => {
    setCurrentRooms(dataRooms?.map((el: any) => el?.id));
  };

  const handleSubmit = () => {
    const newTable = [...system?.state?.table];

    const { name_system } = methods.getValues();

    const result = newTable?.map((el: any) => {
      if (currentRooms?.includes(el?.id)) {
        return {
          ...el,
          [typeSystem]: name_system,
        };
      } else {
        return el;
      }
    });

    system?.id &&
      dispatch(
        airExchangeTableActions.performAirExchangeTable({
          calc_id: system?.id,
          data: { table: result },
        })
      );
    setType('view');
  };

  const downloadReport = () => {
    system?.id &&
      dispatch(
        reportActions.createReport({
          id: system?.id,
          extension: 'xlsx',
        })
      );
  };

  return (
    <Modal
      open={isRender}
      title={getTitle()}
      footer={null}
      closable={false}
      centered
      width={550}
    >
      <FormProvider {...methods}>
        {type === 'view' && (
          <>
            <Table
              columns={columns}
              dataSource={dataSystems()}
              rowKey='nameSystem'
              pagination={false}
            />
            <Button
              className='mt-4'
              type='primary'
              onClick={() => setType('add')}
            >
              Добавить систему
            </Button>
          </>
        )}

        {type === 'add' && (
          <div className='flex flex-col gap-4'>
            <InputForm
              name='name_system'
              label='Наименование системы'
              placeholder='Введите наименование системы'
            />

            <Text>Тип системы</Text>

            <Radio.Group
              options={radioOptions}
              value={typeSystem}
              className='font-normal flex text-center'
              buttonStyle='solid'
              optionType='button'
              onChange={(e) => {
                setCurrentRooms([]);
                setTypeSystem(e?.target?.value);
              }}
            />

            <Table
              columns={roomColumns}
              dataSource={dataRooms}
              rowKey='id'
              pagination={false}
            />

            <div className='flex gap-2'>
              <Button onClick={handleSelectAll}>Выбрать все</Button>
              <Button onClick={() => setCurrentRooms([])}>Отменить все</Button>
            </div>
          </div>
        )}

        <Divider />

        {type === 'add' && (
          <div className='flex gap-2'>
            <Button className='w-1/2' onClick={() => setType('view')}>
              Назад
            </Button>
            <Button
              disabled={!methods.formState.isValid}
              onClick={handleSubmit}
              className='w-1/2'
            >
              Продолжить
            </Button>
          </div>
        )}

        {type === 'view' && (
          <div className='flex gap-2'>
            <Button className='w-1/2' onClick={closeModal}>
              Закрыть
            </Button>
            <Button className='w-1/2' type='primary' onClick={downloadReport}>
              Скачать
            </Button>
          </div>
        )}
      </FormProvider>
    </Modal>
  );
};

export const AirExchangeTableSystemModal: React.FC = () => {
  const { isRender } = useAppSelector(
    airExchangeTableSelectors.airExchangeTableSystemModal
  );

  if (isRender) {
    return <ModalContent />;
  }

  return null;
};
